import { type ApiBaseError } from 'api/utils/shared';
import { useMemo } from 'react';
import useSWR, { useSWRConfig, type SWRConfiguration } from 'swr';
import type {
  StructsGetTagsRequest,
  StructsGetTagsResponse,
  StructsGetTagsWithDetailsResponse,
} from 'types/__generated__/SimInventoryApi';
import { alphabeticSortCompare } from 'utils/strings';
import postFetcher from '../utils/postFetcher';

export interface TagsResultError extends ApiBaseError {}
type TagsSWRConfiguration = SWRConfiguration<StructsGetTagsResponse, TagsResultError>;

interface UseTagsWithDetailsProps {
  filters?: StructsGetTagsRequest;
  canLoad?: boolean;
  onLoadingSlow?: TagsSWRConfiguration['onLoadingSlow'];
}

const useTagsWithDetails = ({
  filters,
  canLoad = true,
  onLoadingSlow,
}: UseTagsWithDetailsProps) => {
  const key =
    canLoad &&
    ((filters?.organizationIds?.length ?? 0) > 0 || (filters?.hologramSimIds?.length ?? 0) > 0)
      ? {
          url: `${process.env.VITE_BIZ_API_ROOT}/sim-inventory/sims/tags/details`,
          body: {
            ...filters,
          },
        }
      : null;

  const config: TagsSWRConfiguration = useSWRConfig();

  if (onLoadingSlow) {
    config.onLoadingSlow = onLoadingSlow;
  }

  const { data, error, isLoading, mutate, isValidating } = useSWR<
    StructsGetTagsWithDetailsResponse,
    TagsResultError
  >(key, postFetcher, config);

  const tags = useMemo(() => {
    if (!data?.tags) {
      return [];
    }

    const newTags = structuredClone(data.tags);
    return newTags.sort((a, b) => alphabeticSortCompare(a?.name ?? '', b?.name ?? ''));
  }, [data]);

  return {
    tags,
    isLoading,
    error,
    mutate,
    isValidating,
  };
};

export default useTagsWithDetails;
